import React, { createContext, useContext, useEffect, useState } from 'react'
import { auth } from '@/lib/apiClient'
import { canRead } from '@/lib/roles'

// replaces @supabase/supabase-js User/Session
export interface AppUser {
  id: string
  email: string
  schema: string
  role: string
  [key: string]: any
}

export interface AppSession {
  access_token: string
  user: AppUser
}

interface AuthContextType {
  user: AppUser | null
  session: AppSession | null
  loading: boolean
  isInternal: boolean
  signOut: () => Promise<void>
  refreshAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null)
  const [session, setSession] = useState<AppSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [isInternal, setIsInternal] = useState(false)

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session: s } } = await auth.getSession()
        if (s?.user) {
          const u = s.user as AppUser
          setSession(s as AppSession)
          setUser(u)
          setIsInternal(canRead(u))
        }
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }

    init()

    // cross-tab session sync via localStorage storage events
    const { data: { subscription } } = auth.onAuthStateChange(async (event, s) => {
      if (event === 'SIGNED_OUT' || !s) {
        setUser(null)
        setSession(null)
        setIsInternal(false)
      } else if (event === 'SIGNED_IN' && s?.user) {
        const u = s.user as AppUser
        setSession(s as AppSession)
        setUser(u)
        setIsInternal(canRead(u))
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const refreshAuth = async () => {
    try {
      const { data: { session: s } } = await auth.getSession()
      if (s?.user) {
        const u = s.user as AppUser
        setSession(s as AppSession)
        setUser(u)
        setIsInternal(canRead(u))
      }
    } catch {
      // silent
    }
  }

  const signOut = async () => {
    try {
      setUser(null)
      setSession(null)
      setIsInternal(false)
      await auth.signOut()
    } finally {
      window.location.href = '/login'
    }
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, isInternal, signOut, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  )
}
